import { createReducer, on } from '@ngrx/store';
import {
  allSearches,
  allSearchesSuccess,
  allSearchesFailure,
  newSearch,
  newSearchSuccess,
  newSearchFailure,
  fetchReport,
  fetchReportSuccess,
  fetchReportFailure,
  archiveReport,
  archiveReportSuccess,
  archiveReportFailure,
  clearReport,
  updateReport,
  allNotesFailure,
  allNotesSuccess,
  allNotes,
  assistantHistory,
  assistantHistorySuccess,
  assistantHistoryFailure,
  askAssistant,
  askAssistantSuccess,
  askAssistantFailure,
  clearAskAssistantResponse,
  deleteNote,
  deleteNoteSuccess,
  deleteNoteFailure,
  clearNotes,
  createNote,
  createNoteSuccess,
  createNoteFailure,
  getNotesSummary,
  getNotesSummarySuccess,
  getNotesSummaryFailure,
  showMoreNotes,
  showMoreNotesSuccess,
  showMoreNotesFailure,
  editNote,
  editNoteSuccess,
  editNoteFailure,
  fetchSelfReport,
  fetchSelfReportSuccess,
  fetchSelfReportFailure,
  restoreReportFailure,
  restoreReportSuccess,
  restoreReport,
  markEventComplete,
  updateIsLoading,
  updateSearchState,
  fuzzySearchFailure,
  fuzzySearchSuccess,
  fuzzySearch,
  clearFuzzySearch,
  updateReportStatusInSearches,
  updateReportStatusInStore,
  updateReportEnrichStatus,
  enrichProfileFailure, enrichProfileSuccess, enrichProfile,
  generateInsights, generateInsightsSuccess, generateInsightsFailure,
  updateRevenue, updateRevenueSuccess, updateRevenueFailure,
  updateContactInfo, updateContactInfoSuccess, updateContactInfoFailure
} from './search.actions';
import { updateUser } from '../auth/auth.actions';

export interface SearchState {
  searches: {
    data?: any[];  // Add the data property with optional type
    current_page?: number;
    total?: number;
    last_page?: number;
    per_page?: number;
    from?: number;
    to?: number;
  };
  report: any | null;
  search: any | null;
  notes: any | null;
  deleted_note: any | null;
  created_note: any | null;
  edited_note: any | null;
  loading_note_delete: any | null;
  loading_note_create: any | null;
  loading_note_edit: any | null;
  loading_notes_summary: any | null;
  notes_summary: any | null;
  error: string;
  isLoading: boolean;

  archived_report: any;
  restored_report: any;
  eventStatuses: {
    [searchId: number]: {
      companyRevenue?: boolean;
      companyProfit?: boolean;
      companyFunding?: boolean;
      companyEmployees?: boolean;
      isLoading?: boolean;
    };
  };

  fuzzySearchLoading: boolean;
  fuzzySearchResults: any[] | null;

  enrichmentLoading: boolean;
  enrichmentData: any | null;
  enrichmentError: any | null;
  reportEnrichStatus: number;

  insightsLoading: boolean;
  insightsData: any | null;
  insightsError: any | null;
  currentInsightType: string | null;

  revenueUpdateLoading: boolean;
  revenueUpdateData: any | null;
  revenueUpdateError: any | null;

  contactInfoUpdateLoading: boolean;
  contactInfoUpdateData: any | null;
  contactInfoUpdateError: any | null;
}

const initialState: SearchState = {
  searches: {},
  report: null,
  search: null,
  notes: null,
  deleted_note: null,
  created_note: null,
  edited_note: null,
  loading_note_delete: false,
  loading_note_create: false,
  loading_note_edit: false,
  loading_notes_summary: false,
  notes_summary: [],
  error: null,
  isLoading: false,
  archived_report: null,
  restored_report: null,
  eventStatuses: {},
  fuzzySearchLoading: false,
  fuzzySearchResults: null,

  enrichmentLoading: false,
  enrichmentData: null,
  enrichmentError: null,
  reportEnrichStatus: 0,

  insightsLoading: false,
  insightsData: null,
  insightsError: null,
  currentInsightType: null,

  revenueUpdateLoading: false,
  revenueUpdateData: null,
  revenueUpdateError: null,

  contactInfoUpdateLoading: false,
  contactInfoUpdateData: null,
  contactInfoUpdateError: null,
};

// @ts-ignore
export const searchReducer = createReducer(
  initialState,

  // Searches
  on(allSearches, (state) => {
    return { ...state, loading: true, error: null };
  }),

  on(allSearchesSuccess, (state, { searches }) => {
    return { ...state, searches, loading: false, error: null };
  }),
  on(allSearchesFailure, (state, { error }) => {
    return { ...state, error, loading: false, token: null };
  }),

  // New Search
  on(newSearch, (state) => {
    return { ...state, loading: true, error: null };
  }),
  on(newSearchSuccess, (state, { search }) => {
    return { ...state, search, loading: false, error: null };
  }),
  on(newSearchFailure, (state, { error }) => {
    return { ...state, error, loading: false, token: null };
  }),

  // Fetch Report
  on(fetchReport, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(fetchReportSuccess, (state, { report }) => ({
    ...state,
    report: { ...report },
    isLoading: false,
    error: null,
  })),
  on(fetchReportFailure, (state, { error }) => ({
    ...state,
    error,
    isLoading: false,
  })),

  // Archive Report
  on(archiveReport, (state) => ({
    ...state,
    archived_report: null,
    isLoading: true,
    error: null,
  })),
  on(archiveReportSuccess, (state, { archived_report }) => ({
    ...state,
    archived_report: archived_report,
    isLoading: false,
    error: null,
  })),
  on(archiveReportFailure, (state, { error }) => ({
    ...state,
    error,
    isLoading: false,
  })),

  // Restore Report
  on(restoreReport, (state) => ({
    ...state,
    restored_report: null,
    isLoading: true,
    error: null,
  })),
  on(restoreReportSuccess, (state, { restored_report }) => ({
    ...state,
    restored_report: restored_report,
    isLoading: false,
    error: null,
  })),
  on(restoreReportFailure, (state, { error }) => ({
    ...state,
    error,
    isLoading: false,
  })),

  // Fetch Self Report
  on(fetchSelfReport, (state) => ({
    ...state,
    isLoading: true,
    error: null,
  })),
  on(fetchSelfReportSuccess, (state, { report }) => ({
    ...state,
    report,
    isLoading: false,
    error: null,
  })),
  on(fetchSelfReportFailure, (state, { error }) => ({
    ...state,
    error,
    isLoading: false,
  })),

  // Update Report
  /*
  on(updateReport, (state, { report }) => ({
    ...state,
    report: report,
  })),
  */
  on(updateReport, (state, { report }) => {
    // Handle both direct report object and nested report.report structure
    const reportData = report.report || report;

    return {
      ...state,
      report: state.report ? {
        ...state.report,
        report: {
          ...state.report.report,
          ...reportData
        }
      } : {
        report: reportData
      }
    };
  }),

  // Hubspot

  // Clear Report
  on(clearReport, (state) => ({
    ...state,
    report: null, // Resetting report to null or your defined initial state
  })),

  // Clear Report

  // Clear Notes
  on(clearNotes, (state) => ({
    ...state,
    notes: null,
    loading_note_create: false,
    loading_note_delete: false,
    created_note: null,
    deleted_note: null,
  })),

  // Clear Report
  on(clearReport, (state) => ({
    ...state,
    report: null, // Clear the report
  })),

  // Notes
  on(allNotes, (state) => {
    return { ...state, loading: true, error: null };
  }),
  on(allNotesSuccess, (state, { notes }) => {
    return { ...state, notes, loading: false, error: null };
  }),
  on(allNotesFailure, (state, { error }) => {
    return { ...state, error, loading: false };
  }),
  on(assistantHistory, (state) => {
    return { ...state, loading: true, error: null };
  }),
  on(assistantHistorySuccess, (state, { history }) => {
    return { ...state, history, loading: false, error: null };
  }),
  on(assistantHistoryFailure, (state, { error }) => {
    return { ...state, error, loading: false };
  }),
  on(askAssistant, (state) => {
    return { ...state, loading_assistant: true, error: null };
  }),
  on(askAssistantSuccess, (state, { response }) => {
    return { ...state, assistant_response: response, loading_assistant: false, error: null };
  }),
  on(askAssistantFailure, (state, { error }) => {
    return { ...state, error, loading_assistant: false };
  }),
  on(clearAskAssistantResponse, (state) => {
    return { ...state, assistant_response: null, loading_assistant: false, error: null };
  }),

  // Delete Note
  on(deleteNote, (state) => {
    return { ...state, loading_note_delete: true, loading: true, error: null };
  }),
  on(deleteNoteSuccess, (state, { deleted_note }) => {
    return {
      ...state,
      deleted_note,
      loading_note_delete: false,
      loading: false,
      error: null,
    };
  }),
  on(deleteNoteFailure, (state, { error }) => {
    return { ...state, loading_note_delete: false, error, loading: false };
  }),

  // Create Note
  on(createNote, (state) => {
    return { ...state, loading_note_create: true, loading: true, error: null };
  }),
  on(createNoteSuccess, (state, { created_note }) => {
    return {
      ...state,
      created_note,
      loading_note_create: false,
      loading: false,
      error: null,
    };
  }),
  on(createNoteFailure, (state, { error }) => {
    return { ...state, loading_note_create: false, error, loading: false };
  }),

  // Notes Summary
  on(getNotesSummary, (state) => {
    return { ...state, loading_note_summary: true, loading: true, error: null };
  }),
  on(getNotesSummarySuccess, (state, { notes_summary }) => {
    return {
      ...state,
      notes_summary,
      loading_note_summary: false,
      loading: false,
      error: null,
    };
  }),
  on(getNotesSummaryFailure, (state, { error }) => {
    return { ...state, loading_note_summary: false, error, loading: false };
  }),

  // Show More Notes
  on(showMoreNotes, (state) => {
    return { ...state, notes: null, loading: true, error: null };
  }),
  on(showMoreNotesSuccess, (state, { notes }) => {
    return { ...state, notes, loading: false, error: null };
  }),
  on(showMoreNotesFailure, (state, { error }) => {
    return { ...state, error, loading: false, hubspot_deals: null };
  }),

  // Edit Note
  on(editNote, (state) => {
    return { ...state, loading_note_edit: true, loading: true, error: null };
  }),
  on(editNoteSuccess, (state, { edited_note }) => {
    return {
      ...state,
      edited_note,
      loading_note_edit: false,
      loading: false,
      error: null,
    };
  }),
  on(editNoteFailure, (state, { error }) => {
    return { ...state, loading_note_edit: false, error, loading: false };
  }),

  // Fuzzy Search
  on(fuzzySearch, (state) => ({
    ...state,
    fuzzySearchLoading: true,
    error: null,
  })),

  on(fuzzySearchSuccess, (state, { results }) => ({
    ...state,
    fuzzySearchResults: results,
    fuzzySearchLoading: false,
    error: null,
  })),

  on(fuzzySearchFailure, (state, { error }) => ({
    ...state,
    error,
    fuzzySearchLoading: false,
  })),

  on(clearFuzzySearch, (state) => ({
    ...state,
    fuzzySearchResults: null, // or []
    fuzzySearchLoading: false,
    error: null,
  })),

  // Mark an event as complete
  on(markEventComplete, (state, { searchId, eventType }) => {
    const eventKeyMap = {
      COMPANY_REVENUE: 'companyRevenue',
      COMPANY_PROFIT: 'companyProfit',
      COMPANY_FUNDING: 'companyFunding',
      COMPANY_EMPLOYEES: 'companyEmployees',
    };

    const currentStatuses = state.eventStatuses[searchId] || {};

    const updatedStatuses = {
      ...currentStatuses,
      [eventKeyMap[eventType]]: true, // Use camelCase key
    };

    return {
      ...state,
      eventStatuses: {
        ...state.eventStatuses,
        [searchId]: updatedStatuses,
      },
    };
  }),

  // Update is_loading directly
  on(updateIsLoading, (state, { searchId, isLoading }) => {
    const currentStatuses = state.eventStatuses[searchId] || {};

    return {
      ...state,
      eventStatuses: {
        ...state.eventStatuses,
        [searchId]: {
          ...currentStatuses,
          isLoading,
        },
      },
    };
  }),

  on(updateSearchState, (state, { searchId, updates }) => {
    // Clone the existing searches data
    const updatedSearches = { ...state.searches };

    // @ts-ignore
    if (updatedSearches.data) {
      // Find and update the specific search by ID
      // @ts-ignore
      updatedSearches.data = updatedSearches.data.map(
        (search: any) =>
          search.id === searchId
            ? { ...search, ...updates } // Merge updates into the specific search
            : search // Leave others unchanged
      );
    }

    // Return the updated state
    return {
      ...state,
      searches: updatedSearches,
    };
  }),

  // In search.reducer.ts
  on(updateReportStatusInSearches, (state, { reportId, statusId }) => {
    if (!state.searches || !state.searches.data) {
      return state;
    }

    // Create a new data array with the updated report
    const updatedData = state.searches.data.map((report: any) => {
      if (Number(report.id) === reportId) {
        return {
          ...report,
          search_status_id: statusId,
          is_enrich: statusId === 2 ? 1 : report.is_enrich  // Also update is_enrich when status is 2
        };
      }
      return report;
    });

    return {
      ...state,
      searches: {
        ...state.searches,
        data: updatedData
      }
    };
  }),

  // Profile Enrichment
  on(enrichProfile, (state) => ({
    ...state,
    enrichmentLoading: true,
    enrichmentError: null,
  })),

  on(enrichProfileSuccess, (state, { data, message }) => ({
    ...state,
    enrichmentLoading: false,
    enrichmentData: data,
    enrichmentError: null,
  })),

  on(enrichProfileFailure, (state, { error }) => ({
    ...state,
    enrichmentLoading: false,
    enrichmentError: error,
  })),

  on(updateReportEnrichStatus, (state, { isEnrich }) => ({
    ...state,
    reportEnrichStatus: isEnrich,
  })),

  on(updateReportStatusInStore, (state, { statusId }) => {
    if (!state.report) {
      return state;
    }

    return {
      ...state,
      report: {
        ...state.report,
        search_status_id: statusId,
      },
    };
  }),

  on(generateInsights, (state, { insightType }) => ({
    ...state,
    insightsLoading: true,
    insightsError: null,
    currentInsightType: insightType,
  })),

  on(generateInsightsSuccess, (state, { data, insightType }) => ({
    ...state,
    insightsLoading: false,
    insightsData: data,
    insightsError: null,
    currentInsightType: insightType,
  })),

  on(generateInsightsFailure, (state, { error }) => ({
    ...state,
    insightsLoading: false,
    insightsError: error,
  })),

  // Update Revenue
  on(updateRevenue, (state) => ({
    ...state,
    revenueUpdateLoading: true,
    revenueUpdateError: null,
  })),

  on(updateRevenueSuccess, (state, { data, message }) => ({
    ...state,
    revenueUpdateLoading: false,
    revenueUpdateData: data,
    revenueUpdateError: null,
  })),

  on(updateRevenueFailure, (state, { error }) => ({
    ...state,
    revenueUpdateLoading: false,
    revenueUpdateError: error,
  })),

  // Update Contact Info
  on(updateContactInfo, (state) => ({
    ...state,
    contactInfoUpdateLoading: true,
    contactInfoUpdateError: null,
  })),

  on(updateContactInfoSuccess, (state, { data, message }) => ({
    ...state,
    contactInfoUpdateLoading: false,
    contactInfoUpdateData: data,
    contactInfoUpdateError: null,
  })),

  on(updateContactInfoFailure, (state, { error }) => ({
    ...state,
    contactInfoUpdateLoading: false,
    contactInfoUpdateError: error,
  })),

);
